<template>
    <div class="admin-users-page">
        <div class="page-header">
            <h2>Users</h2>

            <div class="users-toolbar form-wrapper-one d-flex flex-direction-row align-items-center">
                <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search by name or email..."
                    class="form-control"
                />

                <button
                    class="action-btn delete"
                    @click="runCleanup"
                    :disabled="cleanupLoading"
                >
                    {{ cleanupLoading ? 'Cleaning...' : 'Cleanup Anonymous' }}
                </button>
            </div>
        </div>

        <div v-if="loading" class="state-box">
            Зареждане на users...
        </div>

        <div v-else-if="errorMessage" class="state-box error">
            {{ errorMessage }}
        </div>

        <div v-else class="places-card">
            <div class="places-card-header">
                <h4>Users</h4>
                <span class="places-count">{{ filteredUsers.length }} items</span>
            </div>

            <div v-if="!filteredUsers.length" class="state-box">
                Няма намерени users.
            </div>

            <div v-else class="table-wrap">
                <table class="places-table">
                    <thead>
                    <tr>
                        <th>Avatar</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Provider</th>
                        <th>Pro</th>
                        <th>SOS</th>
                        <th>Reco Today</th>
                        <th>Address</th>
                        <th>FCM</th>
                        <th>Updated</th>
                        <th>Migrated</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="user in filteredUsers" :key="user.id">
                        <td>
                            <div class="photo-cell">
                                <img
                                    v-if="user.avatarUrl"
                                    :src="user.avatarUrl"
                                    alt="avatar"
                                    class="place-photo"
                                    @error="onImageError"
                                />
                                <span v-else>-</span>
                            </div>
                        </td>

                        <td>
                            {{ fullName(user) }}
                        </td>

                        <td>{{ user.email || '-' }}</td>
                        <td>{{ user.provider || '-' }}</td>

                        <td>
                            <span :class="user.isPro ? 'badge yes' : 'badge no'">
                                {{ user.isPro ? 'Yes' : 'No' }}
                            </span>
                        </td>

                        <td>
                            <span :class="user.sos ? 'badge yes' : 'badge no'">
                                {{ user.sos ? 'Active' : 'Off' }}
                            </span>
                        </td>

                        <td>{{ user.recoCountToday ?? 0 }}</td>

                        <td class="user-address">
                            {{ user.address || '-' }}
                        </td>

                        <td class="user-token">
                            {{ shortText(user.fcmToken) }}
                        </td>

                        <td>{{ formatAnyDate(user.updatedAt) }}</td>
                        <td>{{ formatAnyDate(user.migratedAt) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { sentrysDb } from "@/firebase";
import { getAuthToken } from "@/firebase";

export default {
    name: "AdminUsers",

    data() {
        return {
            loading: false,
            errorMessage: "",
            users: [],
            searchQuery: "",
            cleanupLoading: false,
        };
    },

    computed: {
        filteredUsers() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return this.users;

            return this.users.filter((user) => {
                const firstName = (user.firstName || "").toLowerCase();
                const email = (user.email || "").toLowerCase();
                const provider = (user.provider || "").toLowerCase();
                const address = (user.address || "").toLowerCase();

                return firstName.includes(q)
                    || email.includes(q)
                    || provider.includes(q)
                    || address.includes(q);
            });
        }
    },

    async mounted() {
        await this.fetchUsers();
    },

    methods: {
        async fetchUsers() {
            this.loading = true;
            this.errorMessage = "";

            try {
                const snapshot = await getDocs(collection(sentrysDb, "users"));

                this.users = snapshot.docs
                    .map((docItem) => ({
                        id: docItem.id,
                        ...docItem.data(),
                    }))
                    .sort((a, b) => {
                        const aTime = this.toMillis(a.updatedAt);
                        const bTime = this.toMillis(b.updatedAt);
                        return bTime - aTime;
                    });
            } catch (error) {
                console.error("Fetch users error:", error);
                this.errorMessage = "Грешка при зареждане на users.";
            } finally {
                this.loading = false;
            }
        },

        fullName(user) {
            return user.firstName || "-";
        },

        shortText(value, max = 28) {
            if (!value) return "-";
            return value.length > max ? value.slice(0, max) + "..." : value;
        },

        toMillis(value) {
            if (!value) return 0;

            if (typeof value === "number") {
                return value;
            }

            if (value.toDate && typeof value.toDate === "function") {
                return value.toDate().getTime();
            }

            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        },

        formatAnyDate(value) {
            const millis = this.toMillis(value);
            if (!millis) return "-";

            try {
                return new Intl.DateTimeFormat("bg-BG", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                }).format(new Date(millis));
            } catch (error) {
                return "-";
            }
        },

        onImageError(e) {
            e.target.src = "https://via.placeholder.com/60x60?text=User";
        },

        async runCleanup() {
            if (this.cleanupLoading) return;

            const confirmRun = confirm("Сигурен ли си?");
            if (!confirmRun) return;

            this.cleanupLoading = true;

            try {
                const token = await getAuthToken();

                const response = await fetch(
                    "https://us-central1-sentrys-34ab8.cloudfunctions.net/cleanupAnonymousUsers",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            dryRun: false,
                            olderThanMinutes: 60
                        })
                    }
                );

                const data = await response.json();

                if (data.ok) {
                    alert(`Deleted: ${data.deleted}`);
                    await this.fetchUsers();
                } else {
                    alert(data.error);
                }

            } catch (err) {
                console.error(err);
                alert(err.message || "Error");
            } finally {
                this.cleanupLoading = false;
            }
        }
    },
};
</script>

<style lang="scss">
@import './scss/styles.scss';
</style>